import React, {
    useMemo,
    useState,
} from 'react'

import useTitle from '@/hooks/useTitle'

import PulseLoader from 'react-spinners/PulseLoader'

import {
    PlusIcon,
    MinusIcon,
    PencilSquareIcon,
    MagnifyingGlassIcon,
    AdjustmentsHorizontalIcon,
    RectangleStackIcon,
    Squares2X2Icon,
    ArrowPathIcon,
    XMarkIcon,
    TrashIcon,
} from '@heroicons/react/24/outline'

import {
    useGetEvalCategoriesQuery,
    useUpdateEvalCategoryStatusMutation,
    useDeleteEvalCategoryMutation,
} from '@/features/coachUserSessionEvals/evalCategorysApiSlice'

import {
    useGetEvalDimensionsQuery,
    useUpdateEvalDimensionStatusMutation,
    useDeleteEvalDimensionMutation,
} from '@/features/coachUserSessionEvals/evalDimensionsApiSlice'

import EtEvalCategoryForm from './EtEvalCategoryForm'
import EtEvalDimensionForm from './EtEvalDimensionForm'


/*
|--------------------------------------------------------------------------
| 01 - Helpers
|--------------------------------------------------------------------------
*/

const getId = value => {

    if (!value) return ''

    if (typeof value === 'string') {
        return value
    }

    return String(
        value._id || value.id || ''
    )

}


const getEntities = data => {

    if (!data?.ids) return []

    return data.ids
        .map(id => data.entities[id])
        .filter(Boolean)

}


const getErrorMessage = error => {

    return (
        error?.data?.message ||
        error?.error ||
        'Unable to complete the requested operation.'
    )

}


const StatusChip = ({
    active,
}) => (

    <span
        className={`rounded-full px-3 py-1 text-[10px] font-semibold ${
            active
                ? 'bg-lime-100 text-lime-700'
                : 'bg-rose-100 text-rose-700'
        }`}
    >
        {active ? 'Active' : 'Inactive'}
    </span>

)


/*
|--------------------------------------------------------------------------
| 02 - Evaluation Framework Administration
|--------------------------------------------------------------------------
|
| First Created - 29 Oct 2026
| Author - Atul Pandey
|
| Step 12A + 12B
|
| Centralized administration of evaluation categories
| and dimensions through one Trinity Admin menu entry.
|
| CRUD forms are shown inline instead of navigating
| to separate Create/Update pages.
|
|--------------------------------------------------------------------------
*/

const EtEvaluationFrameworkList = () => {

    useTitle('Evaluation Framework')


    /*
    |--------------------------------------------------------------------------
    | 03 - Page State
    |--------------------------------------------------------------------------
    */

    const [
        activeTab,
        setActiveTab,
    ] = useState('categories')

    const [
        searchText,
        setSearchText,
    ] = useState('')

    const [
        statusFilter,
        setStatusFilter,
    ] = useState('all')

    const [
        expandedCategories,
        setExpandedCategories,
    ] = useState({})

    const [
        formMode,
        setFormMode,
    ] = useState(null)

    const [
        editingEntity,
        setEditingEntity,
    ] = useState(null)

    const [
        defaultCategoryId,
        setDefaultCategoryId,
    ] = useState('')

    const [
        errorMessage,
        setErrorMessage,
    ] = useState('')

    const [
        successMessage,
        setSuccessMessage,
    ] = useState('')


    /*
    |--------------------------------------------------------------------------
    | 04 - Master Data Queries
    |--------------------------------------------------------------------------
    |
    | Include inactive records for historical visibility.
    |
    */

    const {
        data: categoryData,
        isLoading: isCategoriesLoading,
        isError: isCategoriesError,
        error: categoriesError,
        refetch: refetchCategories,
    } = useGetEvalCategoriesQuery({
        active: 'all',
    })

    const {
        data: dimensionData,
        isLoading: isDimensionsLoading,
        isError: isDimensionsError,
        error: dimensionsError,
        refetch: refetchDimensions,
    } = useGetEvalDimensionsQuery({
        active: 'all',
    })


    /*
    |--------------------------------------------------------------------------
    | 05 - Status Mutations
    |--------------------------------------------------------------------------
    */

    const [
        updateCategoryStatus,
        {
            isLoading: isUpdatingCategoryStatus,
        },
    ] = useUpdateEvalCategoryStatusMutation()

    const [
        updateDimensionStatus,
        {
            isLoading: isUpdatingDimensionStatus,
        },
    ] = useUpdateEvalDimensionStatusMutation()


    const isUpdatingStatus =
        isUpdatingCategoryStatus ||
        isUpdatingDimensionStatus

    
    /*
    |--------------------------------------------------------------------------
    | 29 Oct 2026 - Hard Delete Mutations
    |--------------------------------------------------------------------------
    */

    const [
        deleteEvalCategory,
        {
            isLoading: isDeletingCategory,
        },
    ] = useDeleteEvalCategoryMutation()

    const [
        deleteEvalDimension,
        {
            isLoading: isDeletingDimension,
        },
    ] = useDeleteEvalDimensionMutation()

    const isDeleting =
        isDeletingCategory ||
        isDeletingDimension


    /*
    |--------------------------------------------------------------------------
    | 06 - Normalized Collections
    |--------------------------------------------------------------------------
    */

    const categories = useMemo(
        () => getEntities(categoryData),
        [categoryData]
    )

    const dimensions = useMemo(
        () => getEntities(dimensionData),
        [dimensionData]
    )


    /*
    |--------------------------------------------------------------------------
    | 07 - Category / Dimension Relationships
    |--------------------------------------------------------------------------
    */

    const dimensionsByCategory = useMemo(() => {

        const map = new Map()

        dimensions.forEach(dimension => {

            const categoryId =
                getId(dimension.categoryId)

            if (!map.has(categoryId)) {
                map.set(categoryId, [])
            }

            map.get(categoryId).push(dimension)

        })

        for (const items of map.values()) {

            items.sort(
                (a, b) =>
                    (a.dimensionName || '')
                        .localeCompare(
                            b.dimensionName || ''
                        )
            )

        }

        return map

    }, [dimensions])


    const categoryById = useMemo(() => {

        return new Map(
            categories.map(category => [
                getId(category),
                category,
            ])
        )

    }, [categories])


    /*
    |--------------------------------------------------------------------------
    | 08 - Counts
    |--------------------------------------------------------------------------
    */

    const categoryCounts = {

        all: categories.length,

        active: categories.filter(
            item => item.active !== false
        ).length,

        inactive: categories.filter(
            item => item.active === false
        ).length,

    }


    const dimensionCounts = {

        all: dimensions.length,

        active: dimensions.filter(
            item => item.active !== false
        ).length,

        inactive: dimensions.filter(
            item => item.active === false
        ).length,

    }


    const currentCounts =
        activeTab === 'categories'
            ? categoryCounts
            : dimensionCounts


    /*
    |--------------------------------------------------------------------------
    | 09 - Filtering
    |--------------------------------------------------------------------------
    */

    const normalizedSearch =
        searchText.trim().toLowerCase()

    const matchesStatus = item => {

        if (statusFilter === 'active') {
            return item.active !== false
        }

        if (statusFilter === 'inactive') {
            return item.active === false
        }

        return true

    }


    const filteredCategories = useMemo(() => {

        return categories
            .filter(category => {

                if (!matchesStatus(category)) {
                    return false
                }

                if (!normalizedSearch) {
                    return true
                }

                const categoryId = getId(category)

                const categoryMatches =
                    [
                        category.categoryName,
                        category.categoryDesc,
                    ]
                        .some(value =>
                            String(value || '')
                                .toLowerCase()
                                .includes(normalizedSearch)
                        )

                const dimensionMatches =
                    (
                        dimensionsByCategory.get(
                            categoryId
                        ) || []
                    ).some(dimension =>
                        [
                            dimension.dimensionName,
                            dimension.dimensionDesc,
                        ]
                            .some(value =>
                                String(value || '')
                                    .toLowerCase()
                                    .includes(normalizedSearch)
                            )
                    )

                return (
                    categoryMatches ||
                    dimensionMatches
                )

            })
            .sort(
                (a, b) =>
                    (a.categoryName || '')
                        .localeCompare(
                            b.categoryName || ''
                        )
            )

    }, [
        categories,
        dimensionsByCategory,
        normalizedSearch,
        statusFilter,
    ])


    const filteredDimensions = useMemo(() => {

        return dimensions
            .filter(dimension => {

                if (!matchesStatus(dimension)) {
                    return false
                }

                if (!normalizedSearch) {
                    return true
                }

                const category =
                    categoryById.get(
                        getId(dimension.categoryId)
                    )

                return [
                    dimension.dimensionName,
                    dimension.dimensionDesc,
                    category?.categoryName,
                ].some(value =>
                    String(value || '')
                        .toLowerCase()
                        .includes(normalizedSearch)
                )

            })
            .sort((a, b) => {

                const categoryA =
                    categoryById.get(
                        getId(a.categoryId)
                    )?.categoryName || ''

                const categoryB =
                    categoryById.get(
                        getId(b.categoryId)
                    )?.categoryName || ''

                return (
                    categoryA.localeCompare(categoryB) ||
                    (a.dimensionName || '')
                        .localeCompare(
                            b.dimensionName || ''
                        )
                )

            })

    }, [
        dimensions,
        categoryById,
        normalizedSearch,
        statusFilter,
    ])


    /*
    |--------------------------------------------------------------------------
    | 10 - Form Navigation
    |--------------------------------------------------------------------------
    */

    const openCreateCategory = () => {

        setActiveTab('categories')
        setFormMode('createCategory')
        setEditingEntity(null)
        setDefaultCategoryId('')
        setErrorMessage('')
        setSuccessMessage('')

    }


    const openUpdateCategory = category => {

        setActiveTab('categories')
        setFormMode('updateCategory')
        setEditingEntity(category)
        setDefaultCategoryId('')
        setErrorMessage('')
        setSuccessMessage('')

    }


    const openCreateDimension = (
        categoryId = ''
    ) => {

        setActiveTab('dimensions')
        setFormMode('createDimension')
        setEditingEntity(null)
        setDefaultCategoryId(categoryId)
        setErrorMessage('')
        setSuccessMessage('')

    }


    const openUpdateDimension = dimension => {

        setActiveTab('dimensions')
        setFormMode('updateDimension')
        setEditingEntity(dimension)
        setDefaultCategoryId('')
        setErrorMessage('')
        setSuccessMessage('')

    }


    const closeForm = () => {

        setFormMode(null)
        setEditingEntity(null)
        setDefaultCategoryId('')
        setErrorMessage('')

    }


    const handleFormDone = () => {

        const completedAction = formMode

        closeForm()

        setSuccessMessage(
            completedAction?.includes('Category')
                ? 'Evaluation category saved successfully.'
                : 'Evaluation dimension saved successfully.'
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 11 - Expand / Collapse Category
    |--------------------------------------------------------------------------
    */

    const toggleCategory = categoryId => {

        setExpandedCategories(previous => ({

            ...previous,

            [categoryId]:
                !previous[categoryId],

        }))

    }


    /*
    |--------------------------------------------------------------------------
    | 12 - Change Status
    |--------------------------------------------------------------------------
    |
    | All changes are confirmed by the user.
    |
    | The backend remains responsible for validating
    | whether a status transition is permitted.
    |
    */

    const handleToggleCategoryStatus =
        async category => {

            const nextActive =
                category.active === false

            const confirmed = window.confirm(
                nextActive
                    ? `Activate category "${category.categoryName}"?`
                    : `Deactivate category "${category.categoryName}"?`
            )

            if (!confirmed) return

            setErrorMessage('')
            setSuccessMessage('')

            try {

                await updateCategoryStatus({

                    id: getId(category),

                    active: nextActive,

                }).unwrap()

                setSuccessMessage(
                    `Category ${nextActive
                        ? 'activated'
                        : 'deactivated'} successfully.`
                )

            } catch (error) {

                setErrorMessage(
                    getErrorMessage(error)
                )

            }

        }


    const handleToggleDimensionStatus =
        async dimension => {

            const nextActive =
                dimension.active === false

            const confirmed = window.confirm(
                nextActive
                    ? `Activate dimension "${dimension.dimensionName}"?`
                    : `Deactivate dimension "${dimension.dimensionName}"?`
            )

            if (!confirmed) return

            setErrorMessage('')
            setSuccessMessage('')

            try {

                await updateDimensionStatus({

                    id: getId(dimension),

                    active: nextActive,

                }).unwrap()

                setSuccessMessage(
                    `Dimension ${nextActive
                        ? 'activated'
                        : 'deactivated'} successfully.`
                )

            } catch (error) {

                setErrorMessage(
                    getErrorMessage(error)
                )

            }

        }
    
    
    /*
    |--------------------------------------------------------------------------
    | 29 Oct 2026 - Hard Delete Evaluation Category
    |--------------------------------------------------------------------------
    |
    | Referential integrity is enforced on the backend.
    |
    |--------------------------------------------------------------------------
    */

    const handleDeleteCategory = async category => {

        const confirmed = window.confirm(
            `Permanently delete evaluation category ` +
            `"${category.categoryName}"?\n\n` +
            `This action cannot be undone.\n\n` +
            `Deletion is allowed only when the category ` +
            `has no associated dimensions or historical references.`
        )

        if (!confirmed) {
            return
        }

        setErrorMessage('')
        setSuccessMessage('')

        try {

            const response = await deleteEvalCategory({

                id: getId(category),

            }).unwrap()

            setSuccessMessage(
                response?.message ||
                'Evaluation category permanently deleted.'
            )

        } catch (error) {

            setErrorMessage(
                getErrorMessage(error)
            )

        }

    }

    /*
    |--------------------------------------------------------------------------
    | 29 Oct 2026 - Hard Delete Evaluation Dimension
    |--------------------------------------------------------------------------
    |
    | Reject deletion when used in any coach evaluation
    | or approved historical sharing snapshot.
    |
    |--------------------------------------------------------------------------
    */

    const handleDeleteDimension = async dimension => {

        const confirmed = window.confirm(
            `Permanently delete evaluation dimension ` +
            `"${dimension.dimensionName}"?\n\n` +
            `This action cannot be undone.\n\n` +
            `If it has been used in any coach evaluation, ` +
            `the server will prevent deletion.`
        )

        if (!confirmed) {
            return
        }

        setErrorMessage('')
        setSuccessMessage('')

        try {

            const response = await deleteEvalDimension({

                id: getId(dimension),

            }).unwrap()

            setSuccessMessage(
                response?.message ||
                'Evaluation dimension permanently deleted.'
            )

        } catch (error) {

            setErrorMessage(
                getErrorMessage(error)
            )

        }

    }

    /*
    |--------------------------------------------------------------------------
    | 13 - Render Helpers
    |--------------------------------------------------------------------------
    */

    const renderDimensionRow = dimension => {

        const category =
            categoryById.get(
                getId(dimension.categoryId)
            )

        return (

            <div
                key={getId(dimension)}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-100 bg-white p-3"
            >

                <div className="flex min-w-0 items-center gap-3">

                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-50">

                        <Squares2X2Icon className="h-4 w-4 text-orange-600" />

                    </div>

                    <div className="min-w-0">

                        <p className="text-[11px] font-bold text-gray-800">
                            {dimension.dimensionName}
                        </p>

                        <p className="mt-1 text-[10px] text-gray-500">
                            {category?.categoryName ||
                                'Uncategorized'}
                        </p>

                        {dimension.dimensionDesc && (

                            <p className="mt-1 text-[10px] text-gray-400">
                                {dimension.dimensionDesc}
                            </p>

                        )}

                    </div>

                </div>


                <div className="flex items-center gap-3">

                    <StatusChip
                        active={
                            dimension.active !== false
                        }
                    />

                    <button
                        type="button"
                        title="Update Dimension"
                        aria-label="Update Dimension"
                        onClick={() =>
                            openUpdateDimension(
                                dimension
                            )
                        }
                        className="rounded-full p-2 hover:bg-orange-50"
                    >
                        <PencilSquareIcon className="h-4 w-4 text-orange-600" />
                    </button>

                     {/* 29 Oct 2026 - Hard Delete Dimension */}

                    <button
                        type="button"
                        title="Permanently Delete Dimension"
                        aria-label={`Delete ${dimension.dimensionName}`}
                        disabled={isDeleting}
                        onClick={() =>
                            handleDeleteDimension(dimension)
                        }
                        className="rounded-full p-2 hover:bg-red-50 disabled:opacity-40"
                    >
                        <TrashIcon className="h-4 w-4 text-red-600" />
                    </button>

                    <button
                        type="button"
                        disabled={isUpdatingStatus}
                        onClick={() =>
                            handleToggleDimensionStatus(
                                dimension
                            )
                        }
                        className="rounded-full border border-gray-200 px-3 py-1 text-[10px] font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    >
                        {dimension.active === false
                            ? 'Activate'
                            : 'Deactivate'}
                    </button>

                   

                </div>

            </div>

        )

    }


    /*
    |--------------------------------------------------------------------------
    | 14 - Loading And Error
    |--------------------------------------------------------------------------
    */

    if (
        isCategoriesLoading ||
        isDimensionsLoading
    ) {

        return (

            <div className="flex justify-center p-5">

                <PulseLoader
                    color="#ea580c"
                    size={7}
                />

            </div>

        )

    }


    if (
        isCategoriesError ||
        isDimensionsError
    ) {

        return (

            <div className="rounded-lg bg-rose-50 p-4 text-xs text-rose-700">

                {getErrorMessage(
                    categoriesError ||
                    dimensionsError
                )}

            </div>

        )

    }


    /*
    |--------------------------------------------------------------------------
    | 15 - Main Render
    |--------------------------------------------------------------------------
    */

    return (

        <div className="mx-auto rounded-lg bg-gray-200 p-2 font-poppins">

            <div className="rounded-lg bg-white p-3">


                {/*------------------------------------------------------
                  Header
                ------------------------------------------------------*/}

                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">

                    <div className="flex items-center gap-3">

                        <AdjustmentsHorizontalIcon className="h-6 w-6 text-orange-600" />

                        <div>

                            <p className="text-xl font-bold text-gray-800">
                                Evaluation Framework
                            </p>

                            <p className="mt-1 text-[10px] italic text-gray-500">
                                Manage the evaluation categories
                                and dimensions used by coaches.
                            </p>

                        </div>

                    </div>

                    <button
                        type="button"
                        onClick={() => {

                            refetchCategories()
                            refetchDimensions()

                        }}
                        className="flex items-center gap-2 rounded-full border border-orange-200 px-3 py-2 text-[10px] font-semibold text-orange-600 hover:bg-orange-50"
                    >

                        <ArrowPathIcon className="h-4 w-4" />

                        Refresh

                    </button>

                </div>


                {/*------------------------------------------------------
                  Tabs
                ------------------------------------------------------*/}

                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-orange-100">

                    <div className="flex gap-2">

                        {[
                            {
                                id: 'categories',
                                label: 'Categories',
                                count: categoryCounts.all,
                            },
                            {
                                id: 'dimensions',
                                label: 'Dimensions',
                                count: dimensionCounts.all,
                            },
                        ].map(tab => (

                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => {

                                    setActiveTab(tab.id)
                                    closeForm()
                                    setSearchText('')
                                    setStatusFilter('all')
                                    setSuccessMessage('')

                                }}
                                className={`flex items-center gap-2 rounded-t-lg px-4 py-3 text-xs font-bold ${
                                    activeTab === tab.id
                                        ? 'border-b-2 border-orange-600 bg-orange-50 text-orange-600'
                                        : 'text-gray-500 hover:bg-orange-50/50'
                                }`}
                            >

                                {tab.label}

                                <span className="rounded-full border border-orange-200 bg-white px-2 py-0.5 text-[9px]">
                                    {tab.count}
                                </span>

                            </button>

                        ))}

                    </div>


                    <button
                        type="button"
                        onClick={
                            activeTab === 'categories'
                                ? openCreateCategory
                                : () => openCreateDimension()
                        }
                        className="mb-2 flex items-center gap-2 rounded-full bg-orange-600 px-4 py-2 text-[10px] font-bold text-white hover:bg-orange-700"
                    >

                        <PlusIcon className="h-4 w-4" />

                        {activeTab === 'categories'
                            ? 'Add Category'
                            : 'Add Dimension'}

                    </button>

                </div>


                {/*------------------------------------------------------
                  Search And Status Filters
                ------------------------------------------------------*/}

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">

                    <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">

                        <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-orange-500" />

                        <input
                            type="text"
                            value={searchText}
                            onChange={event =>
                                setSearchText(
                                    event.target.value
                                )
                            }
                            placeholder={
                                activeTab === 'categories'
                                    ? 'Search categories or dimensions...'
                                    : 'Search dimensions or categories...'
                            }
                            className="w-full bg-transparent text-xs outline-none"
                        />

                        {searchText && (

                            <button
                                type="button"
                                onClick={() =>
                                    setSearchText('')
                                }
                                title="Clear Search"
                            >
                                <XMarkIcon className="h-4 w-4 text-gray-400" />
                            </button>

                        )}

                    </div>


                    <div className="flex flex-wrap gap-2">

                        {[
                            {
                                id: 'all',
                                label: 'All',
                            },
                            {
                                id: 'active',
                                label: 'Active',
                            },
                            {
                                id: 'inactive',
                                label: 'Inactive',
                            },
                        ].map(option => (

                            <button
                                key={option.id}
                                type="button"
                                onClick={() =>
                                    setStatusFilter(
                                        option.id
                                    )
                                }
                                className={`rounded-full border px-3 py-1.5 text-[10px] font-semibold ${
                                    statusFilter === option.id
                                        ? 'border-orange-600 bg-orange-600 text-white'
                                        : 'border-gray-200 bg-white text-gray-600'
                                }`}
                            >

                                {option.label}

                                <span className="ml-1 opacity-75">
                                    {currentCounts[option.id]}
                                </span>

                            </button>

                        ))}

                    </div>

                </div>


                {/*------------------------------------------------------
                  Messages
                ------------------------------------------------------*/}

                {successMessage && (

                    <p className="mt-3 rounded bg-lime-50 p-3 text-[11px] text-lime-700">
                        {successMessage}
                    </p>

                )}

                {errorMessage && (

                    <p role="alert" className="mt-3 rounded bg-rose-50 p-3 text-[11px] text-rose-700">
                        {errorMessage}
                    </p>

                )}


                {/*------------------------------------------------------
                  Inline Create / Update Form
                ------------------------------------------------------*/}

                {formMode && (

                    <div className="mt-4">

                        {(formMode === 'createCategory' ||
                            formMode === 'updateCategory') && (

                            <EtEvalCategoryForm
                                key={
                                    editingEntity?._id ||
                                    'new-category'
                                }
                                category={editingEntity}
                                onDone={handleFormDone}
                                onCancel={closeForm}
                            />

                        )}

                        {(formMode === 'createDimension' ||
                            formMode === 'updateDimension') && (

                            <EtEvalDimensionForm
                                key={
                                    editingEntity?._id ||
                                    `new-dimension-${defaultCategoryId}`
                                }
                                dimension={editingEntity}
                                categories={categories}
                                defaultCategoryId={
                                    defaultCategoryId
                                }
                                onDone={handleFormDone}
                                onCancel={closeForm}
                            />

                        )}

                    </div>

                )}


                {/*------------------------------------------------------
                  Categories Tab
                ------------------------------------------------------*/}

                {activeTab === 'categories' && (

                    <div className="mt-4 space-y-2">

                        {filteredCategories.length === 0 && (

                            <div className="rounded-lg border border-dashed border-gray-200 p-5 text-center">

                                <p className="text-xs text-gray-500">
                                    No evaluation categories match
                                    the selected filters.
                                </p>

                            </div>

                        )}


                        {filteredCategories.map(category => {

                            const categoryId =
                                getId(category)

                            const categoryDimensions =
                                dimensionsByCategory.get(
                                    categoryId
                                ) || []

                            const expanded =
                                expandedCategories[
                                    categoryId
                                ] || false

                            return (

                                <div
                                    key={categoryId}
                                    className="overflow-hidden rounded-lg border border-gray-200 bg-white"
                                >

                                    {/* Category Header */}

                                    <div className="flex flex-wrap items-center justify-between gap-3 bg-gray-50 p-3">

                                        <div className="flex min-w-0 items-center gap-3">

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    toggleCategory(
                                                        categoryId
                                                    )
                                                }
                                                aria-expanded={expanded}
                                                title={
                                                    expanded
                                                        ? 'Hide Dimensions'
                                                        : 'Show Dimensions'
                                                }
                                                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full hover:bg-white"
                                            >

                                                {expanded ? (

                                                    <MinusIcon className="h-5 w-5 text-orange-600" />

                                                ) : (

                                                    <PlusIcon className="h-5 w-5 text-orange-600" />

                                                )}

                                            </button>

                                            <RectangleStackIcon className="h-5 w-5 shrink-0 text-orange-600" />

                                            <div className="min-w-0">

                                                <p className="text-sm font-bold text-gray-800">
                                                    {category.categoryName}
                                                </p>

                                                <p className="mt-1 text-[10px] text-gray-500">
                                                    {categoryDimensions.length}
                                                    {' '}evaluation dimension(s)
                                                </p>

                                                {category.categoryDesc && (

                                                    <p className="mt-1 text-[10px] text-gray-400">
                                                        {category.categoryDesc}
                                                    </p>

                                                )}

                                            </div>

                                        </div>


                                        {/* Category Actions */}

                                        <div className="flex flex-wrap items-center gap-2">

                                            <StatusChip
                                                active={
                                                    category.active !== false
                                                }
                                            />

                                            <button
                                                type="button"
                                                title="Edit Category"
                                                aria-label="Edit Category"
                                                onClick={() =>
                                                    openUpdateCategory(
                                                        category
                                                    )
                                                }
                                                className="rounded-full p-2 hover:bg-orange-50"
                                            >

                                                <PencilSquareIcon className="h-4 w-4 text-orange-600" />

                                            </button>

                                            {/* 29 Oct 2026 - Hard Delete Category */}

                                            <button
                                                type="button"
                                                title="Permanently Delete Category"
                                                aria-label={`Delete ${category.categoryName}`}
                                                disabled={isDeleting}
                                                onClick={() =>
                                                    handleDeleteCategory(category)
                                                }
                                                className="rounded-full p-2 hover:bg-red-50 disabled:opacity-40"
                                            >
                                                <TrashIcon className="h-4 w-4 text-red-600" />
                                            </button>

                                            <button
                                                type="button"
                                                disabled={isUpdatingStatus}
                                                onClick={() =>
                                                    handleToggleCategoryStatus(
                                                        category
                                                    )
                                                }
                                                className="rounded-full border border-gray-200 px-3 py-1 text-[10px] font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                                            >
                                                {category.active === false
                                                    ? 'Activate'
                                                    : 'Deactivate'}
                                            </button>

                                        </div>

                                    </div>


                                    {/* Category Dimensions */}

                                    {expanded && (

                                        <div className="border-t border-gray-100 p-3">

                                            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">

                                                <p className="text-[11px] font-bold text-gray-700">
                                                    Evaluation Dimensions
                                                </p>

                                                <button
                                                    type="button"
                                                    disabled={
                                                        category.active === false
                                                    }
                                                    onClick={() =>
                                                        openCreateDimension(
                                                            categoryId
                                                        )
                                                    }
                                                    className="flex items-center gap-1 rounded-full border border-orange-200 px-3 py-1 text-[10px] font-semibold text-orange-600 hover:bg-orange-50 disabled:opacity-50"
                                                >

                                                    <PlusIcon className="h-4 w-4" />

                                                    Add Dimension

                                                </button>

                                            </div>


                                            {categoryDimensions.length === 0 ? (

                                                <p className="rounded bg-gray-50 p-3 text-[10px] italic text-gray-500">
                                                    No dimensions have been defined
                                                    for this category.
                                                </p>

                                            ) : (

                                                <div className="space-y-2">

                                                    {categoryDimensions
                                                        .filter(dimension => {

                                                            if (!normalizedSearch) {
                                                                return true
                                                            }

                                                            return (
                                                                category.categoryName
                                                                    ?.toLowerCase()
                                                                    .includes(normalizedSearch) ||
                                                                dimension.dimensionName
                                                                    ?.toLowerCase()
                                                                    .includes(normalizedSearch) ||
                                                                dimension.dimensionDesc
                                                                    ?.toLowerCase()
                                                                    .includes(normalizedSearch)
                                                            )

                                                        })
                                                        .map(renderDimensionRow)}

                                                </div>

                                            )}

                                        </div>

                                    )}

                                </div>

                            )

                        })}

                    </div>

                )}


                {/*------------------------------------------------------
                  Dimensions Tab
                ------------------------------------------------------*/}

                {activeTab === 'dimensions' && (

                    <div className="mt-4 space-y-2">

                        <div className="flex items-center justify-between">

                            <p className="text-[11px] font-bold text-gray-700">
                                All Evaluation Dimensions
                            </p>

                            <span className="text-[10px] text-gray-500">
                                {filteredDimensions.length}
                                {' '}result(s)
                            </span>

                        </div>


                        {filteredDimensions.length === 0 ? (

                            <div className="rounded-lg border border-dashed border-gray-200 p-5 text-center">

                                <p className="text-xs text-gray-500">
                                    No evaluation dimensions match
                                    the selected filters.
                                </p>

                            </div>

                        ) : (

                            filteredDimensions.map(
                                renderDimensionRow
                            )

                        )}

                    </div>

                )}


                {/*------------------------------------------------------
                  Historical Integrity Notice
                ------------------------------------------------------*/}

                {/* <div className="mt-5 rounded-lg border border-orange-100 bg-orange-50/40 p-3">

                    <p className="text-[10px] text-gray-600">
                        Evaluation definitions are shared across
                        the Coach Pad and this administration
                        workspace. Deactivation preserves
                        historical evaluation references.
                        Permanent deletion will be enabled only
                        after server-side usage validation
                        is implemented.
                    </p>

                </div> */}

                <div className="mt-5 rounded-lg border border-orange-100 bg-orange-50/40 p-3">

                    <p className="text-[10px] text-gray-600">
                        Evaluation categories and dimensions can be permanently
                        deleted only when they are not referenced by existing
                        definitions or historical coach evaluations.
                        Otherwise, use Deactivate to preserve evaluation history.
                    </p>

                </div>

            </div>

        </div>

    )

}


export default EtEvaluationFrameworkList